import {
  GraphComponent,
  GraphModelManager,
  IEdge,
  IGraph,
  IObjectRenderer,
  IRenderContext,
  IRenderTreeElement,
  IRenderTreeGroup,
  ItemModelManager,
  LabelLayerPolicy,
  ObjectRendererBase,
  PortLayerPolicy,
  SvgVisual,
  SvgVisualGroup,
  Visual
} from '@yfiles/yfiles';
import { backgroundStyle } from './styles';
import { IPort } from '@yfiles/yfiles/yfiles';

type BackgroundVisualRenderTag = {
  product: string;
  hovered: boolean;
};

function isBackgroundVisualRenderTag(tag: any): tag is BackgroundVisualRenderTag {
  return typeof tag.product === 'string' && typeof tag.hovered === 'boolean';
}

function isHovered(tag: any, hoveredEdges: Set<IEdge>): boolean {
  if (tag instanceof IEdge) {
    return hoveredEdges.has(tag);
  } else if (isBackgroundVisualRenderTag(tag)) {
    return tag.hovered;
  }
  return false;
}

function getProductOrder(tag: any, productOrder: Map<string, number>): number {
  if (tag instanceof IEdge) {
    return productOrder.get(tag.tag.product)!;
  } else if (isBackgroundVisualRenderTag(tag)) {
    return productOrder.get(tag.product)!;
  }
  return 9999;
}

class PortManager extends ItemModelManager<IPort> {
  private graphModelManager: GraphModelManager;

  constructor(graphModelManager: GraphModelManager) {
    super(IPort);
    this.graphModelManager = graphModelManager;
  }

  protected getRenderTreeGroup(item: IPort): IRenderTreeGroup | null {
    const ownerGroup = this.graphModelManager.getRenderTreeElement(item.owner)?.parent;
    return ownerGroup ?? null;
  }

  protected install(item: IPort): IRenderTreeElement | null {
    const element = super.install(item);
    element?.lower();
    return element;
  }
}

export class ValueStreamGraphModelManager extends GraphModelManager {
  /**
   * Set of edges that are currently hovered or part of a hovered flow
   */
  public hoveredEdges: Set<IEdge> = new Set<IEdge>();

  /**
   * @param graphComponent The graph component to which this model manager belongs.
   */
  constructor(private readonly graphComponent: GraphComponent) {
    super();
    this.graph = graphComponent.graph;
    this.portLayerPolicy = PortLayerPolicy.AT_OWNER;
    this.nodeLabelLayerPolicy = LabelLayerPolicy.AT_OWNER;
    // Make nodes 3D sortable even though the labels are AT_OWNER
    this.provideRenderTagOnMainRenderTreeElement = true;
  }

  protected createPortModelManager(
    renderer: IObjectRenderer<IPort>,
    callback: (arg: IPort) => IRenderTreeGroup
  ): ItemModelManager<IPort> {
    const manager = new PortManager(this);
    manager.renderer = renderer;
    return manager;
  }

  /**
   * Adds background visuals to all edges in the graph
   */
  public addBackgroundVisuals(): void {
    if (this.edgeGroup && this.graph) {
      addBackgroundVisuals(this.edgeGroup, this.graph, this);
    }
  }

  /**
   * Invalidates the graph component to trigger a repaint.
   * This is necessary to apply z-order changes, for example, after node visibility changes.
   */
  /**
   * Clears the set of hovered edges
   */
  public clearHoveredEdges(): void {
    this.hoveredEdges.clear();
  }

  /**
   * Invalidates the graph component to trigger a repaint.
   * This is necessary to apply z-order changes, for example, after node visibility changes.
   */
  public updateZOrder(): void {
    const productToYCoordinate = new Map<string, number>();
    for (const node of this.graph!.nodes) {
      if (node.tag?.productStart) {
        productToYCoordinate.set(node.tag.product, node.layout.centerY);
      }
    }

    if (this.edgeGroup) {
      const sortedElements = this.edgeGroup.renderTree
        .getElements(this.edgeGroup)
        .toArray()
        .sort((a, b) => {
          const tagA = a.tag;
          const tagB = b.tag;

          if (isHovered(tagA, this.hoveredEdges) && !isHovered(tagB, this.hoveredEdges)) {
            return -1;
          } else if (isHovered(tagB, this.hoveredEdges) && !isHovered(tagA, this.hoveredEdges)) {
            return 1;
          }
          // same hover state
          if (
            getProductOrder(tagA, productToYCoordinate) <
            getProductOrder(tagB, productToYCoordinate)
          ) {
            return -1;
          } else if (
            getProductOrder(tagA, productToYCoordinate) >
            getProductOrder(tagB, productToYCoordinate)
          ) {
            return 1;
          }
          // same product
          if (isBackgroundVisualRenderTag(tagA)) {
            return 1;
          } else if (isBackgroundVisualRenderTag(tagB)) {
            return -1;
          }
          // same product, same hover state, no background visual (i.e., edges in the same product)
          return 0;
        });

      for (const element of sortedElements) {
        element.toBack();
      }
    }

    this.graphComponent.invalidate();
  }
}

class ProductEdgeBackgroundRenderer extends ObjectRendererBase<
  BackgroundVisualRenderTag,
  SvgVisualGroup
> {
  constructor(private readonly hoveredEdges: Set<IEdge>) {
    super();
  }

  private getEdges(graph: IGraph, product: string, hovered: boolean): IEdge[] {
    return graph.edges
      .filter((e) => {
        return e.tag.product === product && hovered === this.hoveredEdges.has(e);
      })
      .toArray();
  }

  createVisual(
    context: IRenderContext,
    { product, hovered }: BackgroundVisualRenderTag
  ): SvgVisualGroup | null {
    const g = new SvgVisualGroup();
    for (const edge of this.getEdges(
      (context.canvasComponent as GraphComponent).graph,
      product,
      hovered
    )) {
      g.add(
        backgroundStyle.renderer
          .getVisualCreator(edge, backgroundStyle)
          .createVisual(context) as SvgVisual
      );
    }
    return g;
  }

  updateVisual(
    context: IRenderContext,
    oldVisual: Visual | null,
    renderTag: BackgroundVisualRenderTag
  ): SvgVisualGroup | null {
    const g = oldVisual as SvgVisualGroup;

    const productEdges = this.getEdges(
      (context.canvasComponent as GraphComponent).graph,
      renderTag.product,
      renderTag.hovered
    );
    if (productEdges.length !== g.children.size) {
      return this.createVisual(context, renderTag);
    }
    productEdges.forEach((edge, i) => {
      g.children.set(
        i,
        backgroundStyle.renderer
          .getVisualCreator(edge, backgroundStyle)
          .updateVisual(context, g.children.get(i)) as SvgVisual
      );
    });
    return g;
  }
}

export function addBackgroundVisuals(
  edgeGroup: IRenderTreeGroup,
  graph: IGraph,
  gmm: ValueStreamGraphModelManager
) {
  // remove old background visuals
  for (const element of edgeGroup.renderTree.getElements(edgeGroup)) {
    if (isBackgroundVisualRenderTag(element.tag)) {
      element.renderTree.remove(element);
    }
  }
  // add new background visuals for each product (twice: one hovered, one not)
  const products = new Set<string>(graph.edges.map((edge) => edge.tag.product));
  for (const product of products) {
    edgeGroup.renderTree.createElement(
      edgeGroup,
      { product, hovered: true },
      new ProductEdgeBackgroundRenderer(gmm.hoveredEdges)
    );
    edgeGroup.renderTree.createElement(
      edgeGroup,
      { product, hovered: false },
      new ProductEdgeBackgroundRenderer(gmm.hoveredEdges)
    );
    gmm.updateZOrder();
  }
}
